/**
 * Sentinel string meaning “no referral id” when merging URL query params with
 * defaults and persisted values (`getUrlParamObj` in `App/Helpers/urlHandlers.js`).
 *
 * @type {string}
 */
export const DEFAULT_REF = '0';

/**
 * Default `refid1`–`refid3` map when those keys are absent from the query string;
 * spread into game URL param resolution in `App/Helpers/urlHandlers.js`.
 *
 * @type {{ refid1: string, refid2: string, refid3: string }}
 */
export const DEFAULT_REF_PARAMS = {
    refid1: DEFAULT_REF,
    refid2: DEFAULT_REF,
    refid3: DEFAULT_REF,
};

/**
 * Merged URL-or-default values in this set are not written to storage and may be
 * replaced by a previously stored value. Used by `shouldPersistGameParam` in
 * `App/Helpers/urlHandlers.js` (`getUrlParamObj`).
 *
 * @type {ReadonlySet<string|undefined|null>}
 */
export const NON_PERSISTABLE_URL_PARAM_VALUES = new Set([
    DEFAULT_REF,
    undefined,
    null,
    '',
]);

/**
 * Decimal places when formatting elapsed seconds in `getTimeDiffInSeconds`
 * (`App/Helpers/generalHandlers.js`).
 *
 * @type {number}
 */
export const DECIMAL_PLACES = 2;

/**
 * Milliseconds per second; paired with {@link DECIMAL_PLACES} in the same
 * duration helper (`App/Helpers/generalHandlers.js`).
 *
 * @type {number}
 */
export const MILLISECONDS_IN_A_SECOND = 1000;

/**
 * Timeout in milliseconds reserved for consent / CookieYes safety logic in
 * `App/Providers/games/ConsentProvider.js` (imported there; usage currently only
 * in commented-out code—keep aligned if re-enabled).
 *
 * @type {number}
 */
export const CONSENT_SAFETY_TIMEOUT_MS = 8000;

/**
 * `localStorage` key for the JSON blob used by `getStorageValue` /
 * `setStorageValue` (`App/Helpers/localStorageHandler.js`). Re-exported as
 * `STORAGE_KEY` from `Pages/games/web/kickandboom/configs/appServicies.js` and
 * `Pages/games/web/bacanagol/configs/appServicies.js`.
 *
 * @type {string}
 */
export const GAME_APP_LOCAL_STORAGE_KEY = 'app_strg';

/**
 * Allowed labels for the Game Analytics custom dimension wired as `values3`
 * (bot detection preset) in `Pages/games/web/kickandboom/configs/ga.js` and
 * `Pages/games/web/bacanagol/configs/ga.js`.
 *
 * @type {readonly string[]}
 */
export const GA_DIMENSION_BOT_PRESET = ['is_bot', 'is_not_bot'];

/**
 * Allowed labels for the Game Analytics custom dimension (`values1` preset)
 * in the same game `configs/ga.js` files.
 *
 * @type {readonly string[]}
 */
export const GA_DIMENSION_PUSH_PRESET = [
    'push_default',
    'push_granted',
    'push_denied',
];

/**
 * Allowed labels for the Game Analytics PWA install custom dimension (`values2` preset) in
 * the same game `configs/ga.js` files.
 *
 * @type {readonly string[]}
 */
export const GA_DIMENSION_PWA_PRESET = [
    'pwa_installed',
    'pwa_not_installed',
    'pwa_not_supported',
];

/**
 * RAM tier labels for a future the Game Analytics custom dimension; overlaps
 * {@link RAM_PROFILES} category strings and adds `Unknown_RAM`.
 *
 * @type {readonly string[]}
 */
export const GA_DIMENSION_RAM_PRESET = [
    'Low_RAM',
    'Medium_Low_RAM',
    'Medium_RAM',
    'High_RAM',
    'Very_High_RAM',
    'Ultra_High_RAM',
    'Unknown_RAM',
];

/**
 * Device RAM bucket thresholds (GB) and category strings produced by
 * `getDeviceRam` / `toCategory` in `App/Helpers/deviceInfo.js`.
 *
 * @type {Readonly<{
 *   LOW: { threshold: number, category: string },
 *   MEDIUMLOW: { threshold: number, category: string },
 *   MEDIUM: { threshold: number, category: string },
 *   HIGH: { threshold: number, category: string },
 *   VERYHIGH: { threshold: number, category: string },
 *   ULTRAHIGH: { threshold: number, category: string },
 * }>}
 */
export const RAM_PROFILES = {
    LOW: { threshold: 2, category: 'Low_RAM' },
    MEDIUMLOW: { threshold: 4, category: 'Medium_Low_RAM' },
    MEDIUM: { threshold: 6, category: 'Medium_RAM' },
    HIGH: { threshold: 8, category: 'High_RAM' },
    VERYHIGH: { threshold: 12, category: 'Very_High_RAM' },
    ULTRAHIGH: { threshold: Infinity, category: 'Ultra_High_RAM' }, // > VERY_HIGH
};

/**
 * Tunables for `getDeviceRam` (`App/Helpers/deviceInfo.js`) when
 * `navigator.deviceMemory` is missing: optional core count / screen pixel
 * heuristics and high-end cutoffs.
 *
 * @type {Readonly<{ universal: {
 *   useCores: boolean,
 *   useScreen: boolean,
 *   hiEndCores: number,
 *   hiEndPixels: number,
 * } }>}
 */
export const RAM_PRESETS = {
    universal: {
        useCores: true, // cheap fallback if deviceMemory is missing
        useScreen: true, // trivial: bump obvious high-end displays
        hiEndCores: 8, // conservative desktop/mobile cutoff
        hiEndPixels: 4_000_000, // ~FHD; avoids over-bumping mid-range phones
    },
};

export const USER_STATUS_PUSH_ONLY = 1;
export const USER_STATUS_PWA_ONLY = 2;
export const USER_STATUS_PUSH_AND_PWA = 3;
